import api from "./api"
import { RegistrarLogErro } from "./clientefiel"

export type DiaSemana = "DOMINGO" | "SEGUNDA" | "TERCA" | "QUARTA" | "QUINTA" | "SEXTA" | "SABADO"

export interface PeriodoEntrega {
    id: number
    diaSemana: DiaSemana | null
    horaInicioTxt: string
    horaFimTxt: string
    tipoEntrega: number
}

export interface PeriodoEntregaPayload {
    diaSemana: DiaSemana | null
    horaInicioTxt: string
    horaFimTxt: string
    tipoEntrega: number
}

export interface DadosPeriodos {
    periodos: PeriodoEntrega[]
    statusDelivery: number
}

export interface ErroApi {
    retornoErro: number
    mensagem: string
}

type RespostaRaw<T> = T & { codErro?: number; mensagem?: string }
type RespostaErroRaw = { codErro: number; mensagem?: string }

function isErroBackend(data: unknown): data is RespostaErroRaw {
    return (
        typeof data === "object" &&
        data !== null &&
        !Array.isArray(data) &&
        "codErro" in data &&
        (data as RespostaErroRaw).codErro > 0
    )
}

function tratamentoResponse<T>(response: { data: RespostaRaw<T> }, chamada: string): T | ErroApi {
    const { data } = response
    if (data.codErro !== undefined && data.codErro > 0) {
        return { retornoErro: data.codErro, mensagem: data.mensagem ?? "" }
    }
    if (typeof data === "string" && data !== "") {
        return {
            retornoErro: 1,
            mensagem: `${chamada} - Erro inesperado, entre em contato com os responsáveis.`,
        }
    }
    return data
}

const ErroRequisicao = function (error: { message?: string }): string | false {
    if (error.message === "Network Error") return "Verifique sua conexão!"
    if (error.message) return "Erro: " + error.message
    return false
}

export const obterPeriodos = async function (
    authorization: string
): Promise<DadosPeriodos | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<DadosPeriodos>>("periodoEntregaFornecedor/ObterPeriodos", {
                headers: { "Content-Type": "application/json", Authorization: authorization },
            })
            .catch(function (error) {
                const msg = ErroRequisicao(error)
                return {
                    data: {
                        codErro: 1,
                        mensagem: msg || "Erro desconhecido",
                    } as RespostaRaw<DadosPeriodos>,
                }
            })

        return tratamentoResponse<DadosPeriodos>(response, "ObterPeriodos")
    } catch (error) {
        RegistrarLogErro(error, "ObterPeriodos", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const adicionarPeriodo = async function (
    authorization: string,
    payload: PeriodoEntregaPayload
): Promise<PeriodoEntrega | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<PeriodoEntrega>>(
                "periodoEntregaFornecedor/AdicionarPeriodo",
                payload,
                {
                    headers: { "Content-Type": "application/json", Authorization: authorization },
                }
            )
            .catch(function (error) {
                const msg = ErroRequisicao(error)
                return {
                    data: {
                        codErro: 1,
                        mensagem: msg || "Erro desconhecido",
                    } as RespostaRaw<PeriodoEntrega>,
                }
            })

        return tratamentoResponse<PeriodoEntrega>(response, "AdicionarPeriodo")
    } catch (error) {
        RegistrarLogErro(error, "AdicionarPeriodo", JSON.stringify(payload))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const atualizarPeriodo = async function (
    authorization: string,
    id: number,
    payload: PeriodoEntregaPayload
): Promise<PeriodoEntrega | ErroApi> {
    try {
        const response = await api
            .put<RespostaRaw<PeriodoEntrega>>(
                `periodoEntregaFornecedor/AtualizarPeriodo/${id}`,
                payload,
                { headers: { "Content-Type": "application/json", Authorization: authorization } }
            )
            .catch(function (error) {
                const msg = ErroRequisicao(error)
                return {
                    data: {
                        codErro: 1,
                        mensagem: msg || "Erro desconhecido",
                    } as RespostaRaw<PeriodoEntrega>,
                }
            })

        return tratamentoResponse<PeriodoEntrega>(response, "AtualizarPeriodo")
    } catch (error) {
        RegistrarLogErro(error, "AtualizarPeriodo", String(id))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const excluirPeriodo = async function (
    authorization: string,
    id: number
): Promise<void | ErroApi> {
    try {
        const response = await api
            .delete<RespostaErroRaw | null>(`periodoEntregaFornecedor/ExcluirPeriodo/${id}`, {
                headers: { "Content-Type": "application/json", Authorization: authorization },
            })
            .catch(function (error) {
                const msg = ErroRequisicao(error)
                return {
                    data: { codErro: 1, mensagem: msg || "Erro desconhecido" } as RespostaErroRaw,
                }
            })

        if (response.data && isErroBackend(response.data)) {
            return { retornoErro: response.data.codErro, mensagem: response.data.mensagem ?? "" }
        }
    } catch (error) {
        RegistrarLogErro(error, "ExcluirPeriodo", String(id))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const atualizarStatusDelivery = async function (
    authorization: string,
    status: number
): Promise<{ statusDelivery: number } | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<{ statusDelivery: number }>>(
                "periodoEntregaFornecedor/AtualizarStatusDelivery",
                { status },
                { headers: { "Content-Type": "application/json", Authorization: authorization } }
            )
            .catch(function (error) {
                const msg = ErroRequisicao(error)
                return {
                    data: { codErro: 1, mensagem: msg || "Erro desconhecido" } as RespostaRaw<{
                        statusDelivery: number
                    }>,
                }
            })

        return tratamentoResponse<{ statusDelivery: number }>(response, "AtualizarStatusDelivery")
    } catch (error) {
        RegistrarLogErro(error, "AtualizarStatusDelivery", String(status))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}
